import { clsx } from "clsx";
import type { ReactNode } from "react";
import { useApprovalStatus } from "../../hooks/useApprovalStatus";

interface GatedPriceProps {
  /** The pre-formatted price text or numeric children. */
  children: ReactNode;
  className?: string;
}

const TOOLTIP =
  "Price hidden — your account is awaiting approval. Once your documents are reviewed, prices unlock.";

/**
 * Wraps a price-bearing element. When the signed-in user isn't fully approved
 * (admins/sellers/approved buyers pass through unchanged), the inner content
 * renders with a CSS blur and a tooltip explaining why.
 *
 * Use this for inline price strings (`QAR 1,200`). For the structured
 * `<CurrencyAmount>` component the same gating is applied internally.
 */
export function GatedPrice({ children, className }: GatedPriceProps) {
  const { status } = useApprovalStatus();
  const gated = status !== "approved";
  if (!gated) {
    return <span className={className}>{children}</span>;
  }
  return (
    <span
      className={clsx(
        "inline-block blur-md select-none cursor-not-allowed",
        className,
      )}
      title={TOOLTIP}
      aria-label="Price hidden — pending approval"
    >
      {children}
    </span>
  );
}

export default GatedPrice;
